'use client'

import React from 'react'
import { Minus, Plus } from 'lucide-react'
import { CheckCircle } from 'solar-icons'
import { useCart } from '@/lib/hooks'
import { handleNumberInput } from '@/lib/utils'

import { Button, DialogClose, Input, Label, Separator } from '@/components/ui'

interface KotFulFillItemProps extends React.ComponentPropsWithoutRef<'form'> {
  item?: any
  cartId?: string
  onSuccess?: () => void
  setUpdateState?: any
}

export const KotFulFillItem = ({
  item,
  cartId,
  onSuccess,
  setUpdateState,
}: KotFulFillItemProps) => {
  const { qty_unfulfilled } = item || {}
  const [qty, setQty] = React.useState<number>(qty_unfulfilled ?? 1)
  const { updateKotCartItem } = useCart()

  const handleSubmit = async (data: FormData) => {
    setUpdateState(true)
    const qty = Number(data.get('qty'))

    updateKotCartItem(
      {
        cartId,
        product_id: item.id,
        qty: qty,
        status_fulfilment: 'fulfilled',
      },
      () => setUpdateState(false),
    )

    onSuccess?.()
  }

  return (
    <form className="flex h-full w-full flex-wrap gap-8" action={handleSubmit}>
      <div className="flex w-full flex-col flex-wrap gap-4 pt-4">
        <div className="flex w-full flex-col gap-5 pb-5">
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full items-center gap-2">
              <Label className="shrink-0 !text-sm font-medium text-gray-700">
                Fulfill Quantity
              </Label>
              <Separator className="mt-1" />
            </div>

            <div className="flex items-center gap-2">
              <Input
                className="w-full appearance-none text-center"
                value={qty}
                inputSize="lg"
                min={1}
                max={qty_unfulfilled}
                name="qty"
                onChangeValue={(value) => setQty(Number(value))}
                onKeyDown={handleNumberInput}
                type="number"
              />
              <div className="flex w-full items-center gap-2">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    if (qty > 1) {
                      setQty((_prevQty) => _prevQty - 1)
                    }
                  }}
                >
                  <Minus className="size-5" strokeWidth={1.5} />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    if (qty < qty_unfulfilled) {
                      setQty((_prevQty) => _prevQty + 1)
                    }
                  }}
                >
                  <Plus className="size-5" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full gap-3 pt-3">
          <DialogClose asChild>
            <Button variant="secondary" className="w-full" size="lg">
              Cancel
            </Button>
          </DialogClose>

          <Button
            variant="brand"
            className="w-full gap-3 shadow-card"
            size="lg"
            type="submit"
          >
            <CheckCircle className="size-5" strokeWidth={1.5} />
            <span>Mark as Fulfilled</span>
          </Button>
        </div>
      </div>
    </form>
  )
}
